
import { Card, CardContent } from "@/components/ui/card";
import { EnhancedButton } from "@/components/ui/enhanced-button";
import { Loader2, Plus, ArrowRight } from "lucide-react";
import { EnhancedDocumentType } from "./enhanced-document-types";
import { useAssessmentEnhancedCreation } from "@/hooks/use-assessment-enhanced-creation";

interface EnhancedCreateNewCardProps {
  type: EnhancedDocumentType;
}

export function EnhancedCreateNewCard({ type }: EnhancedCreateNewCardProps) {
  const { createDocument, isCreating } = useAssessmentEnhancedCreation();

  return (
    <Card className="h-full hover:shadow-xl hover:shadow-cso-100/20 transition-all duration-300 cursor-pointer group border-gray-200 hover:border-cso-200 bg-gradient-to-br from-white to-gray-50/50">
      <CardContent className="p-4 sm:p-6 h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="text-2xl sm:text-3xl group-hover:scale-110 transition-transform duration-200">
            {type.icon}
          </div>
          <div className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            {type.category}
          </div>
        </div>
        
        <h3 className="text-base sm:text-lg font-semibold mb-2 group-hover:text-cso-600 transition-colors line-clamp-2">
          {type.title}
        </h3>
        
        <p className="text-gray-600 text-xs sm:text-sm mb-4 flex-grow line-clamp-3">
          {type.description}
        </p>
        
        <div className="mb-4">
          <div className="text-xs text-gray-500 mb-1">Includes:</div>
          <div className="text-xs text-gray-600 line-clamp-2">
            {type.sections?.slice(0, 3).map(section => section.title).join(', ')}
            {(type.sections?.length || 0) > 3 && '...'}
          </div>
        </div>
        
        <EnhancedButton 
          onClick={() => createDocument(type)}
          disabled={isCreating}
          className="w-full mt-auto group-hover:shadow-lg transition-all duration-200"
          variant="primary"
          size="lg"
        >
          {isCreating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span className="hidden sm:inline">Creating...</span>
              <span className="sm:hidden">Creating</span>
            </>
          ) : (
            <>
              <Plus className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Create {type.title}</span>
              <span className="sm:hidden">Create</span>
              <ArrowRight className="ml-1 h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
            </>
          )}
        </EnhancedButton>
      </CardContent>
    </Card>
  );
}
